import React, { useEffect, useState } from 'react';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import ErrorBoundary from '../ui/ErrorBoundary';
import { Desktop, TabletAndBelow } from '../ui/Responsive';
import { config } from './config/config';
import SearchFilterClearAll from './filters/SearchFilterClearAll';
import SearchFiltersDesktop from './filters/SearchFiltersDesktop';
import SearchFiltersMobile from './filters/SearchFiltersMobile';
import { getSubmarketsSortByCoord } from './helpers/distance';
import { initAlgoliaAnalytics, searchClient } from './helpers/initAlgolia';
import { getIndexFromUrl } from './helpers/routing';
import { routing } from './helpers/searchRouting';
import useFilterString from './hooks/useFilterString';
import useNearbySubmarkets from './hooks/useNearbySubmarkets';
import { useDesktopMediaQuery } from './hooks/useResponsive';
import { useSearchStateFiltersDispatch } from './hooks/useSearchStateFilters';
import { useSearchStateHitsDispatch } from './hooks/useSearchStateHits';
import { useSearchStateIndex, useSearchStateIndexDispatch } from './hooks/useSearchStateIndex';
import { useSearchStateLocation, useSearchStateLocationDispatch } from './hooks/useSearchStateLocation';
import useSearchSubmarkets from './hooks/useSearchSubmarkets';
import GoogleAnalyticsMiddleware from './middleware/googleAnalyticsMiddleware';
import InsightsMiddleware from './middleware/insightsMiddleware';
import PromoSwitcher from './PromoSwitcher';
import SearchHits from './SearchHits';
import SearchHitsOverlay from './SearchHitsOverlay';
import SearchInput from './SearchInput';
import SearchLocationSelector from './SearchLocationSelector';
import SearchMapData from './SearchMapData';
import SearchMapMobile from './SearchMapMobile';
import SearchNoResults from './SearchNoResults';
import SearchFiltersNoResultsBoundary from './SearchNoResultsBoundary';
import SearchSort from './SearchSort';
import SearchTabs from './SearchTabs';

function getRuleContext() {
    const params = new URLSearchParams(window.location.search);
    return params.get('al_context') ? [params.get('al_context')] : [];
}

initAlgoliaAnalytics();
const filterNames = config.FILTER_ATTRIBUTES;

function SearchPage() {
    const [isReady, setIsReady] = useState(false);
    const { submarketsByState, isError } = useSearchSubmarkets();
    const { stateAsLocation, submarket } = useSearchStateLocation();
    const dispatchIndex = useSearchStateIndexDispatch();
    const dispatchLocation = useSearchStateLocationDispatch();
    const dispatchFilters = useSearchStateFiltersDispatch();
    const dispatchHits = useSearchStateHitsDispatch();
    const isDesktop = useDesktopMediaQuery();
    const { activeIndexName } = useSearchStateIndex();
    const { filterString } = useFilterString();
    const { nearbySubmarkets } = useNearbySubmarkets({ submarketsByState });

    // set index from url, other state is synced in onRouteToState
    useEffect(() => {
        if (submarketsByState.length === 0) {
            return;
        }

        const indexFromUrl = getIndexFromUrl();
        const indexForState = indexFromUrl || config.ALGOLIA_INDEX_DEFAULT;
        dispatchIndex({
            type: 'changed',
            index: indexForState,
        });

        setIsReady(true);
    }, [submarketsByState]);

    // happens on first load of page and on back
    const onRouteToState = (routeState) => {
        // console.log( '%conRouteToState', 'background: MediumSeaGreen; color: white; display: block;', routeState, nearbySubmarkets );
        if (!routeState.custom) {
            return;
        }

        const { custom } = routeState;

        // prevents bug where the refinement list is lost from url
        // no idea why this happens
        if (routeState.custom.index !== window.algoliaSearchState.index) {
            dispatchIndex({
                type: 'changed',
                index: routeState.custom.index,
            });
        }

        if (routeState.custom.page) {
            dispatchHits({
                type: 'changePage',
                page: routeState.custom.page,
            });
        }

        const updateStateForLocation = ({ submarketSlug, stateAsLocationAbbr }) => {
            // remove state - reset
            if (!stateAsLocationAbbr && !submarketSlug && !routeState.query && !routeState.custom.cities) {
                //reset cities, etc.
                dispatchLocation({
                    type: 'reset',
                });
                // default to current location if available
                // dup code from hooks, but needs to be in real time and not in state
                //
                if ('geolocation' in navigator) {
                    navigator.geolocation.getCurrentPosition((position) => {
                        const nearby = getSubmarketsSortByCoord({
                            submarketsByState,
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        });
                        if (nearby.length > 0) {
                            dispatchLocation({
                                type: 'selectState',
                                stateAsLocation: {
                                    name: nearby[0].state.name,
                                    abbr: nearby[0].state.alpha_2_code.toLowerCase(),
                                },
                            });
                        }
                    });
                }
                return;
            }

            // add state, remove submarket
            // /az
            if (stateAsLocationAbbr && !submarketSlug) {
                // change stateAsLocation
                dispatchLocation({
                    type: 'selectState',
                    stateAsLocation: {
                        name: submarketsByState.find(
                            (state) => state.alpha_2_code.toLowerCase() === stateAsLocationAbbr
                        ).name,
                        abbr: stateAsLocationAbbr,
                    },
                });
                return;
            }

            // add submarket and add state
            // /az/phoenix-metro
            if (stateAsLocationAbbr && submarketSlug) {
                // change submarket
                const stateFromUrl = submarketsByState.find((state) => {
                    return state.submarkets.find(
                        (sub) => sub.slug === submarketSlug && state.alpha_2_code.toLowerCase() === stateAsLocationAbbr
                    );
                });
                const submarketFromUrl = stateFromUrl.submarkets.find((sub) => sub.slug === submarketSlug);
                if (stateFromUrl) {
                    dispatchLocation({
                        type: 'selectSubmarket',
                        submarket: {
                            submarket: submarketFromUrl.name,
                            state: stateFromUrl.name,
                            stateAbbreviation: stateFromUrl.alpha_2_code,
                            slug: submarketFromUrl.slug,
                        },
                    });
                }
            }
        };

        updateStateForLocation(routeState.custom);

        if (routeState.custom.cities) {
            dispatchLocation({
                type: 'setCities',
                cities: routeState.custom.cities,
            });
        }

        dispatchFilters({
            type: 'changed',
            sqFtRange: [
                custom[filterNames.sqFeetMin] ? parseInt(custom[filterNames.sqFeetMin], 10) : null,
                custom[filterNames.sqFeetMax] ? parseInt(custom[filterNames.sqFeetMax], 10) : null,
            ],
            bedrooms: {
                min: custom[filterNames.bedMin] ? parseInt(custom[filterNames.bedMin], 10) : null,
                max: custom[filterNames.bedMax] ? parseInt(custom[filterNames.bedMax], 10) : null,
            },
            bathrooms: {
                min: custom[filterNames.bathMin] ? parseInt(custom[filterNames.bathMin], 10) : null,
                max: custom[filterNames.bathMax] ? parseInt(custom[filterNames.bathMax], 10) : null,
            },
            garages: {
                min: custom[filterNames.garageMin] ? parseInt(custom[filterNames.garageMin], 10) : null,
                max: custom[filterNames.garageMax] ? parseInt(custom[filterNames.garageMax], 10) : null,
            },
            priceRange: [
                custom[filterNames.priceMin] ? parseInt(custom[filterNames.priceMin], 10) : null,
                custom[filterNames.priceMax] ? parseInt(custom[filterNames.priceMax], 10) : null,
            ],
        });
    };

    if (isError) {
        return <div>There was an error loading the page. Please try again later.</div>;
    }

    if (!activeIndexName || !isReady) {
        return null;
    }

    const hitsPerPage = stateAsLocation.name || submarket.submarket ? 600 : 20;

    return (
        <ErrorBoundary>
            <InstantSearch
                searchClient={searchClient}
                indexName={activeIndexName}
                routing={routing({ onRouteToState })}
            >
                <InsightsMiddleware />
                <GoogleAnalyticsMiddleware />
                <Configure
                    analyticsTags={[
                        config.ANALYTICS_TAGS.searchForm.searchPage,
                        `Device:${isDesktop ? 'Desktop' : 'Mobile'}`,
                    ]}
                    hitsPerPage={hitsPerPage}
                    filters={filterString}
                    clickAnalytics
                    ruleContexts={getRuleContext()}
                />
                <div className="tw-mx-5 lg:tw-ml-10 lg:tw-mr-0 tw-relative" data-insights-index={activeIndexName}>
                    <div className="tw-grid tw-grid-cols-12 tw-gap-5">
                        <div className="tw-col-span-12 lg:tw-col-span-7 tw-mt-14 lg:tw-mt-10">
                            <SearchLocationSelector />
                            <div className="tw-relative">
                                <SearchHitsOverlay />
                                <SearchInput />
                                <SearchTabs />
                                <div className="tw-mt-4">
                                    <SearchFilterClearAll />
                                    <div className={!activeIndexName.includes('neighborhoods') ? 'tw-hidden' : ''}>
                                        <TabletAndBelow>
                                            <div className="tw-mb-6">
                                                <div className="tw-flex tw-justify-between">
                                                    <div className="tw-flex tw-space-x-2">
                                                        <SearchFiltersMobile />
                                                        <SearchMapMobile />
                                                    </div>
                                                    <SearchSort />
                                                </div>
                                            </div>
                                        </TabletAndBelow>
                                        <SearchFiltersDesktop />
                                        <PromoSwitcher />
                                        <SearchFiltersNoResultsBoundary fallback={<SearchNoResults />}>
                                            <SearchHits indexName={activeIndexName} />
                                        </SearchFiltersNoResultsBoundary>
                                    </div>
                                    <div className={!activeIndexName.includes('homes') ? 'tw-hidden' : ''}>
                                        <TabletAndBelow>
                                            <div className="tw-mb-6">
                                                <div className="tw-flex tw-justify-between">
                                                    <div className="tw-flex tw-space-x-2">
                                                        <SearchFiltersMobile />
                                                        <SearchMapMobile />
                                                    </div>
                                                    <SearchSort />
                                                </div>
                                            </div>
                                        </TabletAndBelow>
                                        <SearchFiltersDesktop />
                                        <SearchFiltersNoResultsBoundary fallback={<SearchNoResults />}>
                                            <SearchHits indexName={activeIndexName} />
                                        </SearchFiltersNoResultsBoundary>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tw-hidden tw-sticky tw-top-0 lg:tw-block tw-col-span-5 tw-w-full tw-h-screen tw-bg-platinum">
                            <Desktop>
                                <SearchMapData />
                            </Desktop>
                        </div>
                    </div>
                </div>
            </InstantSearch>
        </ErrorBoundary>
    );
}

export default SearchPage;
